import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { Site } from 'src/app/shared/interfaces/site';

import { DrawingTiltEditorService } from './drawing-tilt-editor.service';
import { DrawingTiltSensor } from './drawing-tilt-sensor.interface';

/**
 * 山留めセンサー図面エディタコンポーネント
 * 現場図面上に山留めセンサーの位置をマーカーで表示・編集
 * 10個以上のセンサーを扱うため、positionName表示のみのシンプルな円形マーカー
 */
@Component({
  selector: 'app-drawing-tilt-editor',
  templateUrl: './drawing-tilt-editor.component.html',
  styleUrls: ['./drawing-tilt-editor.component.scss'],
})
export class DrawingTiltEditorComponent implements OnInit {
  @Input() site: Site = {} as Site;
  @Output() drawingCanceled = new EventEmitter<boolean>();
  @Output() drawingSaved = new EventEmitter<boolean>();

  /** SVGのデフォルトサイズ */
  svgWidth: number = (400 * 19) / 6; // 約1266.67
  svgHeight: number = 400;

  /** SVGの最大サイズ（枠に収めるための制限） */
  private readonly MAX_SVG_WIDTH = (400 * 19) / 6; // 約1266.67
  private readonly MAX_SVG_HEIGHT = 400;

  /** 背景画像が読み込まれているか */
  isImageLoaded = false;

  /** ファイルアップロード用HTML要素 */
  private input = document.createElement('input');

  /** アップロードされた画像ファイル */
  private imageFile: File | null = null;

  /** 山留めセンサー一覧 */
  drawingSensors: DrawingTiltSensor[] = [];

  /**
   * positionName（例: "山留め-1"）から末尾の番号部分のみを取得
   */
  getPositionIndex(sensor: DrawingTiltSensor): string {
    const name = sensor.positionName ?? '';
    const parts = name.split('-');

    // 想定フォーマット "山留め-1" の場合は後半を返す
    if (parts.length >= 2 && parts[1]) {
      return parts[1];
    }

    // フォーマットが違う場合は元の名称をそのまま返す
    return name;
  }

  /** ドラッグ中のセンサー */
  private draggingSensor: DrawingTiltSensor | null = null;
  /** ドラッグ開始時のオフセット */
  private dragOffset = { x: 0, y: 0 };

  /** マーカーの半径 */
  readonly MARKER_RADIUS = 20;

  constructor(private drawingEditorService: DrawingTiltEditorService) {}

  async ngOnInit(): Promise<void> {
    if (!this.site) {
      this.onCancel();
      return;
    }

    // 既存の図面画像を読み込み（drawingTiltプロパティから）
    if (this.site.drawingTilt?.imageUrl) {
      this.setImageBackground(this.site.drawingTilt.imageUrl);
    }

    // 山留めセンサー情報を取得
    this.drawingSensors =
      await this.drawingEditorService.getDrawingSensorsFromSite(this.site);

    this.setupInputElement();
  }

  /**
   * マーカーのドラッグ開始
   */
  onMarkerMouseDown(event: MouseEvent, sensor: DrawingTiltSensor): void {
    event.preventDefault();
    this.draggingSensor = sensor;

    // SVG座標系でのオフセットを計算
    const svg = document.getElementById('svg-tilt') as unknown as SVGSVGElement;
    if (!svg) return;

    const pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;

    const svgP = pt.matrixTransform(ctm.inverse());

    this.dragOffset.x = svgP.x - sensor.x;
    this.dragOffset.y = svgP.y - sensor.y;
  }

  /**
   * SVG上でのマウス移動（ドラッグ中の位置更新）
   */
  onSvgMouseMove(event: MouseEvent): void {
    if (!this.draggingSensor) return;

    event.preventDefault();

    const svg = document.getElementById('svg-tilt') as unknown as SVGSVGElement;
    if (!svg) return;

    const pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;

    const svgP = pt.matrixTransform(ctm.inverse());

    // 新しい位置を計算（マーカーが画面外に出ないよう制限）
    let newX = svgP.x - this.dragOffset.x;
    let newY = svgP.y - this.dragOffset.y;

    newX = Math.max(
      this.MARKER_RADIUS,
      Math.min(this.svgWidth - this.MARKER_RADIUS, newX)
    );
    newY = Math.max(
      this.MARKER_RADIUS,
      Math.min(this.svgHeight - this.MARKER_RADIUS, newY)
    );

    this.draggingSensor.x = newX;
    this.draggingSensor.y = newY;
  }

  /**
   * ドラッグ終了
   */
  onMouseUp(): void {
    this.draggingSensor = null;
  }

  /**
   * 図面画像アップロードボタンクリック
   */
  onUploadClick(): void {
    this.input.click();
  }

  /**
   * キャンセルボタン - 編集を破棄して戻る
   */
  onCancel(): void {
    this.drawingCanceled.emit(true);
  }

  /**
   * 保存ボタン - 図面画像とセンサー位置を保存
   */
  async onSave(): Promise<void> {
    try {
      // 新しい画像がアップロードされていればStorageに保存
      if (this.imageFile) {
        await this.drawingEditorService.uploadDrawingImage(
          this.site.id,
          this.imageFile
        );
      }

      // センサー位置情報をFirestoreに保存
      if (this.drawingSensors.length > 0) {
        await this.drawingEditorService.updateDrawingPositions(
          this.site.id,
          this.drawingSensors
        );
      }

      this.drawingSaved.emit(true);
    } catch (error) {
      console.error('図面保存エラー:', error);
      alert('保存に失敗しました。もう一度お試しください。');
    }
  }

  /**
   * ファイル入力要素の設定
   */
  private setupInputElement(): void {
    this.input.type = 'file';
    this.input.accept = 'image/*';
    this.input.id = 'drawing-tilt-file-input'; // 山留め図面用の一意なID
    this.input.onchange = (event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;

      this.imageFile = file;

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.src = reader.result as string;
        img.onload = () => {
          // 画像のアスペクト比を維持しながら、枠に収まるようSVGサイズを調整
          this.adjustSvgSize(img.width, img.height);

          const svg = document.getElementById('svg-tilt');
          if (svg) {
            svg.style.backgroundImage = `url(${reader.result})`;
            this.isImageLoaded = true;
          }
        };
      };
      reader.readAsDataURL(file);
    };
  }

  /**
   * 既存の図面画像を背景に設定
   */
  private setImageBackground(imageUrl: string): void {
    const img = new Image();
    img.src = imageUrl;

    img.onload = () => {
      // 画像のアスペクト比を維持しながら、枠に収まるようSVGサイズを調整
      this.adjustSvgSize(img.width, img.height);

      setTimeout(() => {
        const svg = document.getElementById('svg-tilt');
        if (svg) {
          svg.style.backgroundImage = `url(${imageUrl})`;
          this.isImageLoaded = true;
        }
      }, 0);
    };
  }

  /**
   * 画像サイズに基づいてSVGサイズを調整
   * アスペクト比を維持しながら、最大サイズ内に収める
   */
  private adjustSvgSize(imageWidth: number, imageHeight: number): void {
    const aspectRatio = imageWidth / imageHeight;

    // 横長の画像の場合
    if (aspectRatio > 1) {
      // 幅を基準にサイズを決定
      if (imageWidth > this.MAX_SVG_WIDTH) {
        this.svgWidth = this.MAX_SVG_WIDTH;
        this.svgHeight = this.svgWidth / aspectRatio;
      } else {
        this.svgWidth = imageWidth;
        this.svgHeight = imageHeight;
      }

      // 高さが最大値を超える場合は高さを基準に再調整
      if (this.svgHeight > this.MAX_SVG_HEIGHT) {
        this.svgHeight = this.MAX_SVG_HEIGHT;
        this.svgWidth = this.svgHeight * aspectRatio;
      }
    } else {
      // 縦長または正方形の画像の場合
      // 高さを基準にサイズを決定
      if (imageHeight > this.MAX_SVG_HEIGHT) {
        this.svgHeight = this.MAX_SVG_HEIGHT;
        this.svgWidth = this.svgHeight * aspectRatio;
      } else {
        this.svgHeight = imageHeight;
        this.svgWidth = imageWidth;
      }

      // 幅が最大値を超える場合は幅を基準に再調整
      if (this.svgWidth > this.MAX_SVG_WIDTH) {
        this.svgWidth = this.MAX_SVG_WIDTH;
        this.svgHeight = this.svgWidth / aspectRatio;
      }
    }
  }
}
